import { create } from "zustand";
import {
  PDA1050_R01CheckedItem,
  PDA1050_R01DetailDto,
  PDA1050_R01Filter,
  PDA1050_R01HeaderDto,
  PDA1050_R01ItemDto,
} from "./PDA1050_R01Dto";

/**
 * Date 객체를 서버 및 date input에서 사용하는 로컬 날짜 형식(yyyy-MM-dd)으로 변환한다.
 * UTC 변환을 사용하지 않아 사용자의 시간대 차이로 날짜가 하루 바뀌는 문제를 방지한다.
 *
 * @param date 변환할 로컬 Date 객체
 * @returns yyyy-MM-dd 형식의 날짜 문자열
 */
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

/** PDA1050_R01 화면에서 공유하는 조회, 선택, 스캔, 검수 상태와 상태 변경 함수 정의 */
export interface Store_PDA1050_R01 {
  isLoading: boolean;
  PDA1050_R01Filter: PDA1050_R01Filter;
  PDA1050_R01List: PDA1050_R01HeaderDto[];
  purchaseOrderNo: string;
  transportDt: string;
  selectedPDA1050_R01: PDA1050_R01HeaderDto | null;
  PDA1050_R01DetailList: PDA1050_R01DetailDto[];
  scannedItem: PDA1050_R01ItemDto | null;
  barcode: string;
  transportQty: number;
  warehouseCd: string;
  warehouseNm: string;
  checkedPDA1050_R01List: PDA1050_R01CheckedItem[];
  setIsLoading: (isLoading: boolean) => void;
  setPDA1050_R01Filter: (filter: Partial<PDA1050_R01Filter>) => void;
  setPDA1050_R01List: (list: PDA1050_R01HeaderDto[]) => void;
  setPurchaseOrderNo: (purchaseOrderNo: string) => void;
  setTransportDt: (transportDt: string) => void;
  setSelectedPDA1050_R01: (header: PDA1050_R01HeaderDto | null) => void;
  setPDA1050_R01DetailList: (list: PDA1050_R01DetailDto[]) => void;
  setScannedItem: (item: PDA1050_R01ItemDto | null) => void;
  setBarcode: (barcode: string) => void;
  setTransportQty: (transportQty: number) => void;
  setWarehouseCd: (warehouseCd: string) => void;
  setWarehouseNm: (warehouseNm: string) => void;
  setCheckedPDA1050_R01List: (list: PDA1050_R01CheckedItem[]) => void;
  /** 다음 품목 스캔을 위해 현재 스캔 품목, 바코드 및 입력 수량만 초기화한다. */
  initScan: () => void;
  /** 화면 이탈 또는 메뉴 재진입 시 검색 조건을 포함한 전체 화면 상태를 초기값으로 되돌린다. */
  initData: () => void;
}

const today = new Date();
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

const initialState = {
  isLoading: false,
  PDA1050_R01Filter: {
    startDate: formatLocalDate(firstDayOfMonth),
    endDate: formatLocalDate(today),
    purchaseOrderNo: "",
  },
  PDA1050_R01List: [],
  purchaseOrderNo: "",
  transportDt: formatLocalDate(new Date()),
  selectedPDA1050_R01: null,
  PDA1050_R01DetailList: [],
  scannedItem: null,
  barcode: "",
  transportQty: 0,
  warehouseCd: "",
  warehouseNm: "",
  checkedPDA1050_R01List: [],
};

/**
 * PDA1050_R01 전역 Store를 생성한다.
 * 단순 setter는 해당 상태만 변경하고, initScan과 initData는 업무 흐름별 초기화 범위를 구분한다.
 */
const usePDA1050_R01Store = create<Store_PDA1050_R01>((set) => ({
  ...initialState,
  setIsLoading: (isLoading) => set({ isLoading }),
  setPDA1050_R01Filter: (filter) =>
    set((state) => ({
      PDA1050_R01Filter: {
        ...state.PDA1050_R01Filter,
        ...filter,
      },
    })),
  setPDA1050_R01List: (PDA1050_R01List) => set({ PDA1050_R01List }),
  setPurchaseOrderNo: (purchaseOrderNo) => set({ purchaseOrderNo }),
  setTransportDt: (transportDt) => set({ transportDt }),
  setSelectedPDA1050_R01: (selectedPDA1050_R01) => set({ selectedPDA1050_R01 }),
  setPDA1050_R01DetailList: (PDA1050_R01DetailList) => set({ PDA1050_R01DetailList }),
  setScannedItem: (scannedItem) => set({ scannedItem }),
  setBarcode: (barcode) => set({ barcode }),
  setTransportQty: (transportQty) => set({ transportQty }),
  setWarehouseCd: (warehouseCd) => set({ warehouseCd }),
  setWarehouseNm: (warehouseNm) => set({ warehouseNm }),
  setCheckedPDA1050_R01List: (checkedPDA1050_R01List) => set({ checkedPDA1050_R01List }),
  initScan: () => set({ scannedItem: null, barcode: "", transportQty: 0 }),
  initData: () => set({ ...initialState, transportDt: formatLocalDate(new Date()) }),
}));

export default usePDA1050_R01Store;
